<template lang="pug">
mixin content()
  div(
    id="modalContent"
    :class="{[$style.modalContainer]: true, [sizeClass]: true, [$style.fixedOnRightSide]: fixedOnRight, [$style.leftSideIsActive]: visibleLeftSide, ['mt-[54px]']: withIcon, [$style.verticallyCentered]: verticallyCentered, [$style.compact]: compact}")
    div(
      v-if="visibleLeftSide"
      :class="$style.leftSideWrapper")
      // @slot The content with attribute 'slot="leftSide"' will be rendered here
      slot(name="leftSide")
    div(:class="{[$style.modalContent]: true, [$style.noPadding]: noPadding}")
      div(
        v-if="showClose"
        :class="$style.closeBtn"
        @click="close()"
        qa-tag="close-modal")
        div(class="inline-block")
          Icon(
            name="i:close-1"
            :size="16")
      .mb-ds-ml.mx-auto.relative.border-gray-400.border.border-solid.bg-white.text-blue-primary.flex.shadow-fbox.rounded-full(
        v-if="withIcon"
        :class="[$style.modalHeadIcon, 'modal-head-icon']")
        .mx-auto.self-center
          Icon(
            :name="icon"
            :size="48")
      .relative
        slot
      div(
        v-if="confirmSubmit"
        :class="$style.confirmation")
        div
          slot(name="submit-confirmation")

transition(
  v-if="transition"
  name="i:slide-fade"
  @before-leave="beforeLeave"
  @after-leave="afterLeave"
)
  template(v-if="classic")
    article(
      v-show="showModal"
      :class="[$style.modalMask, 'bg-black bg-opacity-'+bgOpacity, subModal ? 'sub-modal' : '']"
      @click="close")
      +content()

  template(v-else)
    div(:class="$style.floating")
      +content()
div(v-else)
  article(
    v-if="showModal"
    :class="[$style.modalMask, 'bg-black bg-opacity-'+bgOpacity, subModal ? 'sub-modal' : '']"
    @click="close")
    +content()

</template>

<script>
/** Create a modal dialog */
export default {
  name: "Modal", // eslint-disable-line vue/multi-word-component-names

  props: {
    /** Modal width */
    size: {
      type: String,
      default: "m",
      validator: value => ["s", "m", "l", "xl", "xxl", "auto"].includes(value)
    },
    /** Close button */
    showClose: {
      type: Boolean,
      default: true
    },
    /** Padding 0 in modal content */
    noPadding: {
      type: Boolean,
      default: false
    },
    /** Top icon */
    icon: {
      type: String,
      default: ""
    },
    /** Visible left-side content */
    visibleLeftSide: {
      type: Boolean,
      default: false
    },
    /** Modal fixed on right side of the window */
    fixedOnRight: {
      type: Boolean,
      default: false
    },
    /** Show confirmation layer */
    confirmSubmit: {
      type: Boolean,
      default: false
    },
    bgOpacity: {
      type: Number,
      default: 50,
      validator: value =>
        [0, 5, 10, 20, 25, 30, 40, 50, 60, 70, 75, 80, 90, 95, 100].includes(value)
    },
    verticallyCentered: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    },
    classic: {
      type: Boolean,
      default: true
    },
    transition: {
      type: Boolean,
      default: true
    },
    subModal: {
      type: Boolean,
      default: false
    },
    stopClose: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      showModal: false,
      timerid: null
    }
  },

  computed: {
    modalId() {
      if (typeof this.$el.id === "string" && this.$el.id !== "") {
        return this.$el.id
      }
      return "modal"
    },

    withIcon() {
      return this.icon
    },

    sizeClass() {
      return this.$style[this.size + "Size"]
    }
  },

  mounted() {
    const scrollWidth = Math.round(window.innerWidth - document.documentElement.clientWidth)
    document.documentElement.style.paddingRight = scrollWidth + "px"
    document.addEventListener("keydown", this.keyClose)

    if (this.classic) {
      document.documentElement.style.overflow = "hidden"
      document.documentElement.style.position = "relative"
      document.documentElement.style.height = "100%"
      document.body.style.overflow = "hidden"
      document.body.style.position = "relative"
    }

    // Adding the hash to the url was causing an issue with the router. Trying to navigate back or forward requires
    // and extra click. Removing for now.

    // this.timerid = setTimeout(() => {
    //   if (window.location.hash) {
    //     history.replaceState(null, null, `#${this.modalId}`)
    //   } else {
    //     history.pushState(null, null, `#${this.modalId}`)
    //   }
    // }, 200)

    this.showModal = true
  },

  beforeUnmount() {
    document.documentElement.style.paddingRight = "0"
    document.removeEventListener("keydown", this.keyClose)

    if (this.classic) {
      document.documentElement.style.overflow = "unset"
      document.documentElement.style.position = "static"
      document.documentElement.style.height = "auto"
      document.body.style.overflow = "unset"
      document.body.style.position = "static"
      if (!this.subModal) {
        document.body.style.overflow = "unset"
        document.body.style.position = "static"
      }
    }

    if (window.location.hash && window.location.hash.substring(1) === this.modalId) {
      history.replaceState(null, null, " ")
    }

    if (this.timerid) {
      clearTimeout(this.timerid)
    }
  },

  methods: {
    keyClose(e) {
      if (e.keyCode === 27) {
        this.close()
      }
    },

    close(e, instant = false) {
      if (typeof e === "undefined" || e.target.classList.contains(this.$style.modalMask) || instant) {
        if (typeof e !== "undefined" && !instant) e.stopPropagation()

        // Check if we're on mobile (< 768px) and not forcing instant close
        const isMobile = window.innerWidth < 768

        if (isMobile && !instant) {
          // Mobile: animate then close
          this.showModal = false
          setTimeout(() => {
            this.$emit("close")
          }, 500) // Match the transition duration
        } else {
          // Desktop or instant close: close immediately
          this.showModal = false
          this.$emit("close")
        }
      }
      if (typeof e !== "undefined" && this.stopClose) e.stopPropagation()
    },

    beforeLeave(el) {
      // Ensure the element is visible during animation
      el.style.display = "grid"
    },

    afterLeave(el) {
      // Clean up after animation
      el.style.display = ""
    }
  }
}
</script>

<style lang="scss" module>
$modalWidth: 532px;
$breakpoint-md: var(--breakpoint-md); // fallback if needed

.modalContainer {
  @apply relative bg-white w-full mx-auto flex self-start;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);

  &.withIcon {
    @media (min-width: 1024px) {
      @apply mt-ds-xxl;
    }
  }
}

.modalMask {
  @apply fixed inset-0 grid items-end justify-center overflow-hidden transition-all duration-500;
  z-index: 999;
}

.verticallyCentered {
  @apply self-center;
}

.compact {
  @apply flex-col justify-center bg-white shadow-[0px_8px_16px_0px_rgba(0,0,0,0.25)] transition-transform ease-out;
  position: relative;
  z-index: 1000;
  padding: 0;
  transform: translateY(0);
  width: 100%;
  max-height: calc(100vh - max(env(safe-area-inset-bottom), 24px));
  margin-top: auto;
  border-radius: 16px 16px 0 0;

  @media (min-width: 532px) {
    @apply mx-auto;
    width: $modalWidth !important;
    max-width: $modalWidth !important;
    max-height: 100vh;
    margin-top: 0;
    border-radius: 8px;
    transform: none;
  }
}

.modalContent {
  @apply relative w-full;
  padding: 16px;
  z-index: 1000;

  @media (min-width: 532px) {
    padding: 24px;
  }

  &.noPadding {
    @apply p-0;

    .confirmation {
      @apply px-0 pb-0;

      > div {
        @apply rounded;
      }
    }
  }
}

.modalHeadIcon {
  width: 94px;
  height: 94px;
  z-index: 600;
}

.closeBtn {
  @apply absolute cursor-pointer p-0;
  right: 16px;
  top: 10px;
  z-index: 10;

  @media (min-width: 532px) {
    right: 24px;
  }
}

.confirmation {
  @apply absolute w-full h-full inset-0 p-ds-sm;
  z-index: 500;

  > div {
    background-color: rgba(255, 255, 255, 0.8);
    @apply w-full h-full text-center text-gray-700 text-2xl leading-5xl;
  }
}

.leftSideWrapper {
  img {
    height: 100%;
    vertical-align: middle;
    object-fit: cover;
  }

  @media (min-width: $breakpoint-md) {
    border-top-left-radius: var(--border-radius);
    border-bottom-left-radius: var(--border-radius);
    overflow: hidden;
  }
}

.sSize {
  @media (min-width: $breakpoint-md) {
    .modalContent {
      padding: 24px;
    }
  }
}

.mSize {
  .modalHeadIcon {
    margin-top: -60px;
  }

  @media (min-width: $breakpoint-md) {
    .modalContent {
      padding: 32px;
    }
  }
}

.lSize {
  .modalHeadIcon {
    margin-top: -72px;
  }

  @media (min-width: $breakpoint-md) {
    .modalContent {
      padding: 40px 48px;
    }
  }
}

.xlSize {
  .modalHeadIcon {
    margin-top: -76px;
  }

  @media (min-width: $breakpoint-md) {
    .modalContent {
      padding: 48px;
    }
  }

  @media (min-width: 532px) {
    width: $modalWidth;
  }
}

.autoSize {
  @extend .lSize;

  @media (min-width: $breakpoint-md) {
    width: auto;
    max-width: none;
  }
}

// Modal on Right Panel
.fixedOnRightSide {
  border-radius: 0;
  height: 100%;
  margin-top: 0;
  max-width: $modalWidth;
  right: 0;
  position: fixed;
  overflow: auto;

  > .modalContent {
    padding: 30px 45px;
    display: inline-table;

    > .modalHeadIcon {
      display: none;
    }
  }

  @media (min-width: $modalWidth) {
    width: $modalWidth;
  }
}

// Modal with Left Side Image
.leftSideIsActive {
  display: grid;
  grid-template-columns: 33% auto;

  > .modalContent {
    padding: 60px 26px 40px;

    > .modalHeadIcon {
      margin-top: -106px;
    }
  }
}

// Transition Styles
:global {
  .slide-fade-enter-active,
  .slide-fade-leave-active {
    :local(.modalContainer) {
      transition: all 0.5s cubic-bezier(0, 1, 0.5, 1);
    }
  }

  .slide-fade-enter-from,
  .slide-fade-leave-to {
    :local {
      .modalContainer:not(.fixedOnRightSide) {
        transform: translateY(100%);
        bottom: 0;
      }

      .fixedOnRightSide {
        right: -600px;
      }
    }
  }
}

// Floating Modal
.floating {
  @apply absolute mt-ds-sm z-50;

  .modalContainer {
    @apply mt-0;
  }
}
</style>
